// ═══════════════════════════════════════════
// MonArgent Pro — Database / Storage Layer
// Uses localforage (IndexedDB) for robust offline storage
// Queues changes for server sync when online
// ═══════════════════════════════════════════

import localforage from "localforage";

// Configure localforage
const db = localforage.createInstance({
  name: "MonArgentPro",
  storeName: "app_data",
  description: "MonArgent Pro local database",
});

const syncQueue = localforage.createInstance({
  name: "MonArgentPro",
  storeName: "sync_queue",
  description: "Transactions waiting to sync",
});

// ─── Core CRUD ───
export async function save(key, value) {
  try {
    await db.setItem(key, value);
    return true;
  } catch (err) {
    console.error("[DB] Save failed:", key, err);
    return false;
  }
}

export async function load(key, defaultValue = null) {
  try {
    const val = await db.getItem(key);
    return val !== null ? val : defaultValue;
  } catch (err) {
    console.error("[DB] Load failed:", key, err);
    return defaultValue;
  }
}

export async function remove(key) {
  try {
    await db.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export async function clearAll() {
  try {
    await db.clear();
    await syncQueue.clear();
    return true;
  } catch {
    return false;
  }
}

// ─── Transaction-specific helpers ───
export async function saveTransaction(txn) {
  // Save to local DB
  const transactions = await load("transactions", []);
  transactions.unshift(txn);
  await save("transactions", transactions);

  // Add to sync queue (for when server is available)
  await addToSyncQueue("transaction", txn);

  return transactions;
}

export async function getTransactions(filters = {}) {
  const transactions = await load("transactions", []);

  let filtered = transactions;

  if (filters.date) {
    filtered = filtered.filter((t) => t.date === filters.date);
  }
  if (filters.employeeId) {
    filtered = filtered.filter((t) => t.employeeId === filters.employeeId);
  }
  if (filters.type) {
    filtered = filtered.filter((t) => t.type === filters.type);
  }
  if (filters.dateFrom) {
    filtered = filtered.filter((t) => t.date >= filters.dateFrom);
  }
  if (filters.dateTo) {
    filtered = filtered.filter((t) => t.date <= filters.dateTo);
  }

  return filtered;
}

export async function getStats(filters = {}) {
  const txns = await getTransactions(filters);
  const ventes = txns.filter((t) => t.type === "vente");
  const depenses = txns.filter((t) => t.type === "depense");

  return {
    totalVentes: ventes.reduce((sum, t) => sum + t.amount, 0),
    totalDepenses: depenses.reduce((sum, t) => sum + t.amount, 0),
    benefice: ventes.reduce((sum, t) => sum + t.amount, 0) - depenses.reduce((sum, t) => sum + t.amount, 0),
    nbTransactions: txns.length,
    nbVentes: ventes.length,
    nbDepenses: depenses.length,
    transactions: txns,
  };
}

// ─── Sync Queue ───
async function addToSyncQueue(type, data) {
  try {
    const queue = (await syncQueue.getItem("pending")) || [];
    queue.push({
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      type,
      data,
      timestamp: Date.now(),
      synced: false,
    });
    await syncQueue.setItem("pending", queue);
  } catch (err) {
    console.error("[Sync] Queue add failed:", err);
  }
}

export async function getSyncQueue() {
  return (await syncQueue.getItem("pending")) || [];
}

export async function clearSyncQueue() {
  await syncQueue.setItem("pending", []);
}

export async function syncWithServer(serverUrl) {
  // This would connect to your backend API
  // For now, it's a placeholder for future server integration
  const queue = await getSyncQueue();
  if (queue.length === 0) return { synced: 0 };

  try {
    // In production:
    // const response = await fetch(`${serverUrl}/api/sync`, {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ transactions: queue }),
    // });
    // if (response.ok) { await clearSyncQueue(); }

    console.log(`[Sync] ${queue.length} items ready to sync`);
    return { synced: 0, pending: queue.length };
  } catch (err) {
    console.error("[Sync] Failed:", err);
    return { synced: 0, pending: queue.length, error: err.message };
  }
}

// ─── Config ───
export async function saveConfig(config) {
  return save("config", config);
}

export async function getConfig() {
  return load("config", null);
}

// ─── Notifications log ───
export async function saveNotification(notification) {
  const notifs = await load("notifications", []);
  notifs.unshift(notification);
  // Keep only last 50
  if (notifs.length > 50) notifs.length = 50;
  await save("notifications", notifs);
  return notifs;
}

export async function getNotifications() {
  return load("notifications", []);
}

// ─── Export data (for backup) ───
export async function exportAllData() {
  const config = await getConfig();
  const transactions = await load("transactions", []);
  const notifications = await getNotifications();

  return {
    version: "1.0.0",
    exportDate: new Date().toISOString(),
    config,
    transactions,
    notifications,
  };
}

export async function importData(data) {
  if (data.config) await saveConfig(data.config);
  if (data.transactions) await save("transactions", data.transactions);
  if (data.notifications) await save("notifications", data.notifications);
  return true;
}

export default {
  save, load, remove, clearAll,
  saveTransaction, getTransactions, getStats,
  saveConfig, getConfig,
  saveNotification, getNotifications,
  getSyncQueue, clearSyncQueue, syncWithServer,
  exportAllData, importData,
};
